import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  TextInput,
  Button,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Title,
  Divider,
  Blockquote,
} from "@mantine/core";
import ShipDataTable from "./ShipDataTable";
import PlotlyGraphs from "./PlotlyGraphs";
import { useNavigate } from "react-router-dom";
import { ProjectDataType, ShipDataType } from "@/types/apiTypes";
import { notifications } from "@mantine/notifications";
import { Text } from "@/components/Generics/Text";
import {
  getButtonStyles,
  getDeleteButtonStyles,
} from "@/helpers/getButtonStyles";
import { getFormStyles } from "@/helpers/getFormStyles";
import { useFetchProjects } from "@/hooks/apiHooks/useFetchProjects";
import { useAssociateShipToProject } from "@/hooks/apiHooks/useAssociateShipToProject";
import { useRemoveShipFromProject } from "@/hooks/apiHooks/useRemoveShipFromProject";
import { useEditShip } from "@/hooks/apiHooks/useEditShip";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";
import styles from "../../css/shipDetails.module.css";
import { CustomSwitch } from "@/components/Generics/CustomSwitch";
import { useFetchSingleShip } from "@/hooks/apiHooks/useFetchSingleShip";

const ShipDetails: React.FC = () => {
  const location = useLocation();
  const shipId = location.state?.shipId;
  const navigate = useNavigate();

  const { isDarkMode, colors } = useAppColors();
  const buttonStyles = getButtonStyles(isDarkMode);
  const deleteButtonStyles = getDeleteButtonStyles(isDarkMode);
  const formStyles = getFormStyles(isDarkMode);

  const rawShip = location.state.shipData as Partial<ShipDataType>;

  const { state } = useLocation(); // { shipId, shipData }
  const { data: shipData, isLoading } = useFetchSingleShip(
    state.shipId,
    state.shipData,
  );

  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const { data: allProjects = [] } = useFetchProjects();
  const [associatedProjects, setAssociatedProjects] = useState<
    ProjectDataType[]
  >([]);
  const [isProjectUpdateModalOpen, setIsProjectUpdateModalOpen] =
    useState(false);

  // console.log("ShipData -> " + JSON.stringify(shipData));

  const {
    mutateAsync: editShipMutation,
    // isPending: isEditing
  } = useEditShip();
  const associateShipMutation = useAssociateShipToProject();
  const removeShipMutation = useRemoveShipFromProject();

  const [editableFields, setEditableFields] = useState({
    ship_name: "",
    load_case_name: "",
    comment: "",
    additional_info: "",
    locked: false,
  });
  useEffect(() => {
    if (shipData) {
      setEditableFields({
        ship_name: shipData.ship_name ?? "",
        load_case_name: shipData.load_case_name ?? "",
        comment: shipData.comment ?? "",
        additional_info: shipData.additional_info ?? "",
        locked: shipData.locked,
      });
    }
  }, [shipData]);

  // Fetch ship data, projects, and associated studies
  useEffect(() => {
    if (!shipId) {
      console.error("No ship data available");
      navigate("/");
      return;
    }
    if (shipData) {
      if (shipData.projects) {
        const projectIds = shipData.projects.map((project) => project.id);
        setSelectedProjects(projectIds);
        setAssociatedProjects(shipData.projects);
      }
    }
  }, [shipId, shipData, allProjects, navigate]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const [confirmLock, setConfirmLock] = useState(false);
  const [locked, setLocked] = useState(!!shipData?.locked);

  useEffect(() => {
    if (!shipData?.locked) {
      handleFieldChange("locked", locked);
    } else if (shipData?.locked) {
      setLocked(true);
    }
  }, [locked]);

  // const handleEditField = (fieldName: string) => {
  //   if (shipData.locked) {
  //     notifications.show({
  //       title: "Edit Disabled",
  //       message: "This ship is locked and cannot be edited.",
  //       color: "red",
  //     });
  //     return;
  //   }
  //   setEditMode((prevState) => ({
  //     ...prevState,
  //     [fieldName]: true,
  //   }));
  // };

  const handleUpdateClick = async () => {
    try {
      if (shipData) {
        await editShipMutation({
          shipId: shipData.id,
          updatedData: editableFields,
        });
      }
    } catch (error) {
      console.error("Error updating ship data:", error);
    }
  };

  const handleProjectSelectionChange = (selected: string[]) => {
    setSelectedProjects(selected);
    setIsProjectUpdateModalOpen(true);
  };

  const confirmProjectAssociation = async () => {
    setIsProjectUpdateModalOpen(false);
    const addedProjects = selectedProjects.filter(
      (id) => !associatedProjects.map((p) => p.id).includes(id),
    );
    const removedProjects = associatedProjects
      .map((p) => p.id)
      .filter((id) => !selectedProjects.includes(id));

    try {
      if (shipData) {
        for (const projectId of addedProjects) {
          associateShipMutation.mutate({ ship: shipData, projectId });
        }

        for (const projectId of removedProjects) {
          removeShipMutation.mutate({ ship: shipData, projectId: projectId });
        }
      }

      setAssociatedProjects(
        selectedProjects.map((id) => allProjects.find((p) => p.id === id)!),
      );
      /*
      notifications.show({
        title: "Success",
        message: "Project association updated!",
        color: "green",
        autoClose: 4000,
        position: "top-center",
      });
       */
    } catch (error) {
      console.error("Failed to update project association", error);
      notifications.show({
        title: "Error",
        message: "An error occurred while updating project association.",
        color: "red",
        autoClose: 4000,
        position: "top-center",
      });
    }
  };

  // const handleRemoveShipFromProject = async (projectId: string) => {
  //   try {
  //     const token = await getToken();
  //     if (token) {
  //       await axios.delete(
  //         `${API_BASE_URL}/projects/${projectId}/ships/${shipData.id}`,
  //         { headers: { Authorization: `Bearer ${token}` } },
  //       );
  //       notifications.show({
  //         title: "Ship removed from project",
  //         message: `${shipData.ship_name} has been removed from the project.`,
  //         color: "green",
  //       });
  //       setAssociatedProjects(
  //         associatedProjects.filter((project) => project.id !== projectId),
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Failed to remove ship from project", error);
  //     notifications.show({
  //       title: "Error",
  //       message: "An error occurred while removing the ship from the project.",
  //       color: "red",
  //     });
  //   }
  // };
  //
  // const handleDeleteShip = () => {
  //   if (shipData.locked) {
  //     setDeleteShipId(shipData.id);
  //     setIsDeleteModalOpen(true);
  //   } else {
  //     confirmDeleteShip(shipData.id);
  //   }
  // };
  //
  // const confirmDeleteShip = async (shipId: string) => {
  //   try {
  //     const response = await context?.deleteShip(shipId);
  //     if (response?.status === "success") {
  //       setIsDeleteModalOpen(false);
  //       setDeleteShipId(null);
  //       notifications.show({
  //         title: "Deleted successfully!",
  //         message: response.message,
  //         color: "green",
  //       });
  //       navigate("/");
  //     } else if (response?.status === "fail") {
  //       setIsDeleteModalOpen(false);
  //       setDeleteShipId(null);
  //       setIsAssociatedStudiesModalOpen(true);
  //     } else {
  //       notifications.show({
  //         title: "Error",
  //         message: "An error occurred while deleting the ship.",
  //         color: "red",
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error deleting ship:", error);
  //     notifications.show({
  //       title: "Error",
  //       message: "An error occurred while deleting the ship.",
  //       color: "red",
  //     });
  //   }
  // };

  // if (isEditing || transition) return null;

  return (
    <div className={styles.container}>
      {shipData ? (
        <>
          <div className={styles.gridContainer}>
            <div
              className={styles.innerContainer}
              style={{
                backgroundColor: colors.gray,
                borderRadius: ".5rem",
                padding: "1rem",
                boxShadow: "3px 3px 1rem rgba(0, 0, 0, .1)",
                height: "fit-content",
              }}
            >
              <div>
                <Group mb="md" justify="space-between">
                  <Title
                    size="xl"
                    mt="md"
                    mb="sm"
                    style={{ color: colors.secondary }}
                  >
                    General Information
                  </Title>
                  <Button
                    onClick={handleUpdateClick}
                    disabled={shipData.locked}
                    style={[
                      buttonStyles,
                      { opacity: shipData.locked ? 0.4 : 1 },
                    ]}
                  >
                    Update
                  </Button>
                </Group>

                {(
                  [
                    "ship_name",
                    "load_case_name",
                    "comment",
                    "additional_info",
                  ] as const
                ).map((field) => (
                  <TextInput
                    key={field}
                    label={field.replace(/_/g, " ")}
                    value={editableFields[field]}
                    onChange={(event) =>
                      handleFieldChange(field, event.currentTarget.value)
                    }
                    disabled={shipData.locked}
                    mb="md"
                    styles={formStyles}
                  />
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <CustomSwitch
                    value={locked}
                    action={(newVal) => {
                      if (!shipData.locked) {
                        setConfirmLock(newVal);
                        setLocked(newVal);
                      }
                    }}
                    disabled={shipData.locked}
                    type="lock"
                  />
                  <Text size="medium" color={colors.secondary}>
                    {editableFields.locked ? "Locked ✔" : "Lock Ship"}
                  </Text>
                </div>

                <Divider my="md" />
                <Title
                  size="xl"
                  mt="md"
                  mb="sm"
                  style={{ color: colors.secondary }}
                >
                  Project Association
                </Title>
                <MultiSelect
                  label="Projects"
                  placeholder="Select projects"
                  data={allProjects
                    .filter(
                      (p) => !associatedProjects.some((ap) => ap.id === p.id),
                    )
                    .map((p) => ({ value: p.id, label: p.name }))}
                  value={selectedProjects.map((p) => p)}
                  onChange={handleProjectSelectionChange}
                  styles={{
                    ...formStyles,
                    pill: {
                      display: "none",
                    },
                  }}
                  searchable
                />

                <Stack>
                  {associatedProjects.length > 0 ? (
                    associatedProjects.map((project) => (
                      <Group key={project.id} justify="space-between">
                        <Text color={colors.secondary}>📂 {project.name}</Text>
                        <Button
                          variant="light"
                          size="xs"
                          onClick={() =>
                            removeShipMutation.mutate({
                              ship: shipData,
                              projectId: project.id,
                            })
                          }
                          style={{
                            ...deleteButtonStyles,
                            marginTop: "1rem",
                            backgroundColor: colors.red,
                          }}
                        >
                          Remove
                        </Button>
                      </Group>
                    ))
                  ) : (
                    <Text size="small" color={colors.secondary}>
                      No projects assigned
                    </Text>
                  )}
                </Stack>
                <Divider my="md" />

                <Title size="xl" mb="sm" style={{ color: colors.secondary }}>
                  Used in Studies
                </Title>

                {shipData.studies.length ? (
                  <Stack>
                    {shipData.studies.map((study) => (
                      <Button
                        key={study.id}
                        variant="subtle"
                        style={{ textAlign: "left" }}
                        onClick={() => navigate(`/study-details/${study.id}`)}
                      >
                        {study.experiment_name}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Text size="small" color={colors.secondary}>
                    This ship is not used in any study yet.
                  </Text>
                )}
              </div>
            </div>

            <div
              className={styles.innerContainer}
              style={{
                backgroundColor: colors.gray,
                borderRadius: ".5rem",
                padding: "1rem",
                boxShadow: "3px 3px 1rem rgba(0, 0, 0, .1)",
                marginBottom: "9rem",
              }}
            >
              <ShipDataTable data={shipData.vessel_data} />
              <Divider my="md" />
              <PlotlyGraphs data={shipData.vessel_data} />
            </div>
          </div>

          {/* LOCK SHIP MODAL */}
          <Modal
            opened={confirmLock}
            onClose={() => setConfirmLock(false)}
            title="Lock Ship"
          >
            <Blockquote color="blue">
              Do you want to lock this ship? This action cannot be undone.
            </Blockquote>
            <div className={styles.btnContainer}>
              <button
                className={styles.button}
                style={{
                  backgroundColor: colors.darkGray,
                  color: colors.primary,
                }}
                onClick={() => {
                  setConfirmLock(false);
                  setLocked(false);
                }}
              >
                Cancel
              </button>
              <button
                className={styles.button}
                style={{ backgroundColor: colors.brand, color: colors.primary }}
                onClick={() => {
                  setConfirmLock(false);
                  handleUpdateClick();
                  navigate("/");
                }}
              >
                Confirm
              </button>
            </div>
          </Modal>

          {/* Project Update Modal */}
          <Modal
            opened={isProjectUpdateModalOpen}
            onClose={() => setIsProjectUpdateModalOpen(false)}
            title="Confirm Project Association"
          >
            <Blockquote color="blue">
              <Text>
                Do you want to update the selected projects for this ship?
              </Text>
            </Blockquote>
            <Group mt="md">
              <div className={styles.btnContainer}>
                <button
                  onClick={() => setIsProjectUpdateModalOpen(false)}
                  className={styles.button}
                  style={{
                    backgroundColor: colors.darkGray,
                    color: colors.primary,
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={confirmProjectAssociation}
                  className={styles.button}
                  style={{
                    backgroundColor: colors.brand,
                    color: colors.primary,
                  }}
                >
                  Update
                </button>
              </div>
            </Group>
          </Modal>
        </>
      ) : (
        <Text>Loading ship data...</Text>
      )}
    </div>
  );
};

export default ShipDetails;
